import { createReducer, on } from '@ngrx/store';
import {
  FeatureFlagsServiceActions,
  FeatureFlagsFallbackActions,
} from './feature-flags.actions';
import {
  FeatureFlags,
  createDefaultFeatures,
} from '../../core/models/feature-flags.model';

/** Feature Flags State */
export interface FeatureFlagsState {
  /** Current flag values */
  flags: FeatureFlags;
  /** Whether LaunchDarkly client is ready */
  ready: boolean;
  /** Whether initialization is in progress */
  loading: boolean;
  /** Error message if initialization failed */
  error: string | null;
}

const initialState: FeatureFlagsState = {
  flags: createDefaultFeatures(),
  ready: false,
  loading: false,
  error: null,
};

/** Feature Flags Reducer */
export const featureFlagsReducer = createReducer(
  initialState,

  // Initialization started
  on(FeatureFlagsServiceActions.init, (state): FeatureFlagsState => ({
    ...state,
    loading: true,
    error: null,
  })),

  // LaunchDarkly client ready
  on(FeatureFlagsServiceActions.ready, (state): FeatureFlagsState => ({
    ...state,
    ready: true,
    loading: false,
  })),

  // Initialization failed
  on(
    FeatureFlagsServiceActions.initFailed,
    (state, { error }): FeatureFlagsState => ({
      ...state,
      ready: false,
      loading: false,
      error,
    })
  ),

  // Single flag value update
  on(
    FeatureFlagsServiceActions.updateFlag,
    (state, { domain, property, value }): FeatureFlagsState => {
      // Type-safe dynamic property access using Record type
      const flags = state.flags as Record<string, Record<string, unknown>>;

      // Validate domain exists - silent fail for invalid domains
      if (!(domain in flags)) {
        return state;
      }

      // Validate property exists in domain - silent fail for invalid properties
      if (!(property in flags[domain])) {
        return state;
      }

      // Immutable update
      return {
        ...state,
        flags: {
          ...state.flags,
          [domain]: {
            ...flags[domain],
            [property]: value,
          },
        } as FeatureFlags,
      };
    }
  ),

  // Bulk update all flags at once
  on(
    FeatureFlagsServiceActions.bulkUpdate,
    (state, { flags }): FeatureFlagsState => ({
      ...state,
      flags,
    })
  ),

  // Load from static defaults
  on(
    FeatureFlagsFallbackActions.loadFromDefaults,
    (state, { flags }): FeatureFlagsState => ({
      ...state,
      flags,
      ready: true,
      loading: false,
      error: null,
    })
  )
);
